import { useState } from 'react';
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

/**
 * SDK-injected user content (a loaded skill body, a system reminder, etc.)
 * — collapsed by default so it doesn't masquerade as user chat. Reads
 * "context" rather than a normal message bubble.
 */
export function InjectedContent({
  label,
  body,
  className,
}: {
  label: string;
  body: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn(
        'min-w-0 max-w-full overflow-hidden rounded-md border border-dashed bg-muted/20 text-muted-foreground',
        className,
      )}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-xs"
        >
          {open ? (
            <ChevronDown className="size-3 shrink-0" />
          ) : (
            <ChevronRight className="size-3 shrink-0" />
          )}
          <Sparkles className="size-3 shrink-0" />
          <span className="truncate">{label}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-dashed px-2.5 py-1.5">
        <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px]">
          {body}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Heuristics for detecting SDK-injected text. Returns a label when the
 * text looks injected, null otherwise.
 *  - "Base directory for this skill:" — Claude Code injects this header
 *    when a skill is loaded.
 *  - `<system-reminder>...</system-reminder>` — system reminders.
 *  - `<command-name>...</command-name>` — slash command invocations the
 *    SDK echoes into the transcript.
 */
export function classifyInjected(text: string): string | null {
  if (text.startsWith('Base directory for this skill:')) return 'Skill loaded';
  if (text.includes('<system-reminder>')) return 'System reminder';
  if (text.includes('<command-name>')) return 'Slash command';
  if (text.startsWith('<local-command-')) return 'Local command output';
  return null;
}
